import { plain, text } from './format'
import type { Line } from './types'

export type Filter = {
  name: string
  usage: string
  summary: { en: string; ru: string }
  apply: (input: Line[], args: string[]) => { lines: Line[]; status: number }
}

const count = (args: string[], fallback: number): number => {
  const flag = args.findIndex((value) => value === '-n')
  const inline = args.find((value) => /^-\d+$/.test(value))
  const raw = flag >= 0 ? args[flag + 1] : inline?.slice(1)
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback
}

/**
 * Pipeline stages. Everything downstream of a `|` operates on rendered lines,
 * which is exactly what a real shell does with a text stream.
 */
export const filters: Record<string, Filter> = {
  grep: {
    name: 'grep',
    usage: 'grep [-i] [-v] <pattern>',
    summary: { en: 'keep lines matching a pattern', ru: 'оставить строки по шаблону' },
    apply(input, args) {
      const flags = args.filter((value) => value.startsWith('-'))
      const pattern = args.filter((value) => !value.startsWith('-')).join(' ')
      if (!pattern) return { lines: [text('grep: missing pattern', 'err')], status: 2 }

      const insensitive = flags.some((flag) => flag.includes('i'))
      const invert = flags.some((flag) => flag.includes('v'))
      const needle = insensitive ? pattern.toLowerCase() : pattern
      const lines = input.filter((row) => {
        const haystack = insensitive ? plain(row).toLowerCase() : plain(row)
        return haystack.includes(needle) !== invert
      })
      return { lines, status: lines.length ? 0 : 1 }
    },
  },

  head: {
    name: 'head',
    usage: 'head [-n <count>]',
    summary: { en: 'first lines of the stream', ru: 'первые строки потока' },
    apply: (input, args) => ({ lines: input.slice(0, count(args, 10)), status: 0 }),
  },

  tail: {
    name: 'tail',
    usage: 'tail [-n <count>]',
    summary: { en: 'last lines of the stream', ru: 'последние строки потока' },
    apply: (input, args) => ({ lines: input.slice(-count(args, 10)), status: 0 }),
  },

  wc: {
    name: 'wc',
    usage: 'wc [-l|-w|-c]',
    summary: { en: 'count lines, words or bytes', ru: 'счётчик строк, слов и байтов' },
    apply(input, args) {
      const body = input.map(plain)
      const lines = body.length
      const words = body.reduce((total, row) => total + row.split(/\s+/).filter(Boolean).length, 0)
      const bytes = body.reduce((total, row) => total + row.length + 1, 0)
      if (args.includes('-l')) return { lines: [text(String(lines))], status: 0 }
      if (args.includes('-w')) return { lines: [text(String(words))], status: 0 }
      if (args.includes('-c')) return { lines: [text(String(bytes))], status: 0 }
      return {
        lines: [text(`${String(lines).padStart(6)}${String(words).padStart(8)}${String(bytes).padStart(8)}`)],
        status: 0,
      }
    },
  },

  sort: {
    name: 'sort',
    usage: 'sort [-r]',
    summary: { en: 'sort lines alphabetically', ru: 'отсортировать строки' },
    apply(input, args) {
      const sorted = [...input].sort((a, b) => plain(a).localeCompare(plain(b)))
      return { lines: args.includes('-r') ? sorted.reverse() : sorted, status: 0 }
    },
  },

  uniq: {
    name: 'uniq',
    usage: 'uniq',
    summary: { en: 'drop repeated adjacent lines', ru: 'убрать повторяющиеся строки' },
    apply(input) {
      const lines = input.filter((row, index) => index === 0 || plain(row) !== plain(input[index - 1]))
      return { lines, status: 0 }
    },
  },

  nl: {
    name: 'nl',
    usage: 'nl',
    summary: { en: 'number the lines', ru: 'пронумеровать строки' },
    apply(input) {
      return {
        lines: input.map((row, index) => ({
          ...row,
          spans: [{ text: `${String(index + 1).padStart(5)}  `, style: 'dim' as const }, ...row.spans],
        })),
        status: 0,
      }
    },
  },
}

export const filterNames = Object.keys(filters)
